// Venue-search VS1.6: the house-rules document field. Which files go in, and
// what each failure says. The server checks the real bytes again
// (functions/helper/house-rules.js) and makes every file a PDF/A.

export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024; // the same in storage.rules

// The kinds the server converts, by file ending.
const DOCUMENT_EXT = { pdf: 'pdf', docx: 'docx', doc: 'doc', pages: 'pages', odt: 'odt', rtf: 'rtf', txt: 'txt', text: 'txt' };
// Photos go through the venue photo code first (any photo → an upright JPEG, no GPS).
const PHOTO_EXT = /^(jpe?g|jfif|png|webp|heic|heif)$/;
// Windows, WhatsApp and email can lose the ending or send an empty type, so
// the type counts too when there is no known ending.
const DOCUMENT_TYPE = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'application/vnd.apple.pages': 'pages',
  'application/x-iwork-pages-sffpages': 'pages',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'text/plain': 'txt'
};
const PHOTO_TYPE = /^image\/(jpe?g|pjpeg|png|webp|heic|heif)$/i;

// For the file picker. Endings, plus types for phones that read only types.
export const ACCEPT = [
  '.pdf,.docx,.doc,.pages,.odt,.rtf,.txt',
  '.jpg,.jpeg,.png,.heic,.heif,.webp',
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text,application/rtf,text/plain',
  'image/jpeg,image/png,image/webp,image/heic,image/heif'
].join(',');

const endingOf = (name) => {
  const match = /\.([^./\\]+)$/.exec(String(name || ''));
  return match ? match[1].toLowerCase() : '';
};

// {kind: 'photo'} | {kind: 'document', ext} | null (cannot be used).
export const kindOf = (file) => {
  const ending = endingOf(file.name);
  if (DOCUMENT_EXT[ending]) return { kind: 'document', ext: DOCUMENT_EXT[ending] };
  if (PHOTO_EXT.test(ending)) return { kind: 'photo' };
  if (ending) return null;
  const type = String(file.type || '').toLowerCase();
  if (DOCUMENT_TYPE[type]) return { kind: 'document', ext: DOCUMENT_TYPE[type] };
  if (PHOTO_TYPE.test(type)) return { kind: 'photo' };
  return null;
};

// Why this file cannot be used, or null. Photos are made smaller first, so
// only documents have the 10 MB limit here (photoUpload.js has its own).
export const fileProblem = (file) => {
  const kind = kindOf(file);
  if (!kind) return 'kind';
  if (kind.kind === 'document' && Number(file.size) > MAX_DOCUMENT_BYTES) return 'size';
  return null;
};

// 32 random hex characters: never the original name, which can hold private
// facts or text written for an AI. The ending tells the converter the kind.
export const randomUploadName = (ext) =>
  `${Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('')}.${ext}`;

// A failure → the key of its words in venueEditor.js (houseRules.errors).
export const errorKey = (error) => {
  const code = String((error && error.code) || '');
  if (code === 'kind' || code === 'size') return code;
  if (code === 'upload' || code.endsWith('/deadline-exceeded')) return 'network';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'network';
  // The converter is down or starting: the file may be fine.
  if (code.endsWith('/unavailable') || code.endsWith('/internal') || code.endsWith('/resource-exhausted')) return 'busy';
  return 'unreadable';
};
